import type { StudyState } from '@sigora/domain';
import type { StudySummary } from '../api/client.ts';
import { AppHeader } from '../ui/AppHeader.tsx';
import type { HeaderAccount } from '../ui/HeaderMenu.tsx';
import { DeleteStudyControl } from './DeleteStudyControl.tsx';

/** One word per lifecycle state, in the researcher's vocabulary rather than the database's. */
const STATE_LABELS: Record<StudyState, string> = {
  draft: 'Draft',
  ready: 'Ready',
  live: 'Fielding',
  analyzing: 'Analysing',
  complete: 'Complete',
  under_recruited: 'Under-recruited',
  cancelled: 'Cancelled',
};

/**
 * What the list says a study is.
 *
 * One derivation on top of the state: a `ready` study whose lock is set is mid set-up. It is
 * permanently undeletable, it is on its way into Remesh, and calling it Ready would invite a
 * delete the database refuses. No eighth state is introduced for it — the lock is already the
 * fact, and this is the one place it is worth a word of its own.
 */
export const studyStatusLabel = ({
  state,
  lockedAt,
}: Pick<StudySummary, 'state' | 'lockedAt'>): string =>
  state === 'ready' && lockedAt !== null ? 'Setting up' : STATE_LABELS[state];

export interface StudyListProps {
  studies: readonly StudySummary[];
  loading: boolean;
  error: string | null;
  creating: boolean;
  onOpen: (studyId: string) => void;
  onNew: () => void;
  /** Read the list again: a deleted row is gone on the server and has to go from the screen. */
  onDeleted: () => void;
  /** Who is signed in and the control that ends it, in the header's menu. */
  account: HeaderAccount;
}

/**
 * Where a researcher arrives and where they come back to: every study they have, newest first,
 * and the one button that starts another.
 *
 * One column, deliberately. The guided experience is narrow and focused all the way through, so
 * the list is written once at that width rather than as a grid that collapses into one.
 */
export const StudyList = ({
  studies,
  loading,
  error,
  creating,
  onOpen,
  onNew,
  onDeleted,
  account,
}: StudyListProps) => (
  <div className="study-home">
    <AppHeader account={account} />
    <main className="studies" data-test-id="study-list">
      <div className="studies-header">
        <h1>Your studies</h1>
        <button
          className="button-primary"
          type="button"
          data-test-id="new-study"
          disabled={creating}
          onClick={onNew}
        >
          {creating ? 'Starting…' : 'New study'}
        </button>
      </div>

      {error !== null && (
        <p className="chat-error" data-test-id="study-list-error" role="alert">
          {error}
        </p>
      )}

      {loading ? (
        <p data-test-id="study-list-loading">Loading…</p>
      ) : studies.length === 0 ? (
        // Said rather than left blank: an empty list and a list that has not arrived look the same,
        // and the answer to the first is the button above it.
        <p data-test-id="study-list-empty">
          You have no studies yet. Start one and it will appear here.
        </p>
      ) : (
        <ul>
          {studies.map((study) => (
            <li className="study-list-card" key={study.id}>
              <button
                type="button"
                className="study-list-title"
                data-test-id={`open-study-${study.id}`}
                onClick={() => onOpen(study.id)}
              >
                {study.title}
              </button>
              <span
                className={`artifact-status study-status-${study.state}`}
                data-test-id={`study-status-${study.id}`}
              >
                {studyStatusLabel(study)}
              </span>
              {study.deletable && (
                <DeleteStudyControl
                  studyId={study.id}
                  title={study.title}
                  testId={`delete-study-${study.id}`}
                  onDeleted={onDeleted}
                />
              )}
            </li>
          ))}
        </ul>
      )}
    </main>
  </div>
);
